import React from 'react';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
// material
import { styled } from '@mui/material/styles';
import { Link, Stack, Typography, TextField, IconButton, InputAdornment, Box, Alert } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useNotificationsProvider } from '../../context/NotificationsContext';
// component
import Iconify from '../../components/Iconify';
import Logo from '../../components/Logo';

import { reset2FAAccept } from './actions'

// ----------------------------------------------------------------------
const LoginStyle = styled('div')(({ theme }) => ({
  maxWidth: 480,
  margin: 'auto',
  display: 'flex',
  minHeight: '100vh',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.spacing(12, 0)
}));

const ContainerStyle = styled(Box)(({ theme }) => ({
  shadowColor: theme.palette.secondary,
  backgroundColor: theme.palette.grey,
  shadowOpacity: 0.8,
  shadowRadius: 2,
  shadowOffset: {
    height: 1,
    width: 1
  },
  elevation: 5,
}));

const LogoStyle = {
  width: 175,
  height: 48
};

export default function Reset2FAAcceptForm() {
  const { t } = useTranslation()
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { showNotification } = useNotificationsProvider();
  const [searchParams] = useSearchParams();

  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError] = React.useState(null);

  // Get token and email from URL params
  const token = searchParams.get('token');
  const email = searchParams.get('email');

  // Redirect if missing params
  React.useEffect(() => {
    if (!token || !email) {
      setError(t('reset-2fa-accept.missing-params'));
    }
  }, [token, email, t]);

  const Reset2FAAcceptSchema = Yup.object().shape({
    email: Yup.string().email(t('reset-2fa-accept.invalid-email')).required(t('reset-2fa-accept.required-email')),
    password: Yup.string().required(t('reset-2fa-accept.required-password'))
  });

  const formik = useFormik({
    initialValues: {
      email: email || '',
      password: '',
    },
    enableReinitialize: true,
    validationSchema: Reset2FAAcceptSchema,
    onSubmit: (data) => {
      if (!token) {
        setError(t('reset-2fa-accept.missing-token'));
        return;
      }
      setError(null);
      dispatch(reset2FAAccept(token, data.email, data.password, () => {
        setSubmitting(false);
        showNotification('success', t('reset-2fa-accept.success'));
        navigate('/login');
      }, (translationKey) => {
        setSubmitting(false);
        setError(t(translationKey));
      }))
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps, setSubmitting } = formik;

  const handleShowPassword = () => {
    setShowPassword((show) => !show);
  };

  return (
    <LoginStyle>
      <ContainerStyle>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Stack alignItems="center">
                <Logo sx={LogoStyle} />
              </Stack>
              <Typography variant="h4">
                {t('reset-2fa-accept.title')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('reset-2fa-accept.subtitle')}
              </Typography>

              <Alert severity="warning">
                {t('reset-2fa-accept.warning')}
              </Alert>

              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}

              <TextField
                fullWidth
                autoComplete="username"
                type="email"
                label={t('reset-2fa-accept.email-label')}
                {...getFieldProps('email')}
                error={Boolean(touched.email && errors.email)}
                helperText={touched.email && errors.email}
                disabled={!!email} // Disable if email comes from URL
              />

              <TextField
                fullWidth
                autoComplete="current-password"
                type={showPassword ? 'text' : 'password'}
                label={t('reset-2fa-accept.password-label')}
                {...getFieldProps('password')}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleShowPassword} edge="end">
                        <Iconify icon={showPassword ? 'eva:eye-fill' : 'eva:eye-off-fill'} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={Boolean(touched.password && errors.password)}
                helperText={touched.password && errors.password}
              />

              <Stack direction="column" alignItems="center" justifyContent="space-between" spacing={2} sx={{ my: 2 }}>
                <LoadingButton size="large" type="submit" variant="contained" color="error" loading={isSubmitting} disabled={!token}>
                  {t('reset-2fa-accept.submit-button')}
                </LoadingButton>
                <Link component={RouterLink} variant="subtitle2" to="/reset-2fa" underline="hover">
                  {t('reset-2fa-accept.link-expired')}
                </Link>
                <Link component={RouterLink} variant="subtitle2" to="/login" underline="hover">
                  {t('reset-2fa-accept.back-to-login')}
                </Link>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </ContainerStyle>
    </LoginStyle>
  );
};
